import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  onSelectAll?: () => void;
  onDelete?: () => void;
  onRename?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onEscape?: () => void;
  onEnter?: () => void;
}

/**
 * Hook for managing keyboard shortcuts
 * Binds common file manager shortcuts
 */
export function useKeyboardShortcuts(
  handlers: KeyboardShortcutHandlers,
  enabled: boolean = true
) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isMeta = event.metaKey || event.ctrlKey;
      const target = event.target as HTMLElement;

      // Don't trigger shortcuts when typing in inputs
      const isInputFocused =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Allow Enter and Escape even in inputs for some cases
      if (isInputFocused && !['Enter', 'Escape'].includes(event.key)) {
        return;
      }

      // Cmd/Ctrl + A - Select all
      if (isMeta && event.key === 'a' && handlers.onSelectAll) {
        event.preventDefault();
        handlers.onSelectAll();
        return;
      }

      // Cmd/Ctrl + C - Copy
      if (isMeta && event.key === 'c' && handlers.onCopy) {
        event.preventDefault();
        handlers.onCopy();
        return;
      }

      // Cmd/Ctrl + V - Paste
      if (isMeta && event.key === 'v' && handlers.onPaste) {
        event.preventDefault();
        handlers.onPaste();
        return;
      }

      // NOTE: Delete key is intentionally NOT bound to delete files
      // Users must use right-click context menu or other UI to delete files
      // This prevents accidental deletion

      // Enter - Rename single selection
      if (event.key === 'Enter' && handlers.onEnter && !isInputFocused) {
        event.preventDefault();
        handlers.onEnter();
        return;
      }

      // Escape - Clear selection or close modals
      if (event.key === 'Escape' && handlers.onEscape) {
        event.preventDefault();
        handlers.onEscape();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlers, enabled]);
}
